import { useState } from 'react';
import { api, sizeLabel, usd } from '../lib/api';
import { ErrorNote, Modal, useLoad } from '../components/ui';
import NewItemButton from '../components/NewItemButton';

// Customer groups (a percent off and/or a price list) and the price lists they use.
export default function Groups() {
  const groups = useLoad(() => api<any[]>('/api/customer-groups'), []);
  const lists = useLoad(() => api<any[]>('/api/price-lists'), []);
  const [editGroup, setEditGroup] = useState<any>(null);
  const [editList, setEditList] = useState<any>(null);
  const [open, setOpen] = useState<any>(null);
  const [err, setErr] = useState<unknown>(null);
  const reload = () => { groups.reload(); lists.reload(); };
  const guard = async (fn: () => Promise<unknown>) => { setErr(null); try { await fn(); reload(); } catch (e) { setErr(e); } };
  return (
    <div className="stack">
      <div className="page-head"><div className="grow"><h1>Customer groups and price lists</h1>
        <p>A group gives its customers a percent off, a price list, or both. Put a customer in a group on the Customers page. At the register the group price comes off first, then promotions and coupons work on what is left. A price list never raises a price, and nothing goes below your legal cost.</p></div></div>
      <ErrorNote error={groups.error ?? lists.error ?? err} />

      <div className="panel"><div className="panel-body">
        <div className="row"><h2 className="grow" style={{ margin: 0 }}>Groups</h2><button className="primary" onClick={() => setEditGroup({})}>New group</button></div>
        <div className="table-wrap"><table className="grid">
          <thead><tr><th>Name</th><th className="n">% off</th><th>Price list</th><th className="n">Customers</th><th /></tr></thead>
          <tbody>{groups.data?.map((g) => (
            <tr key={g.id}><td>{g.name}</td><td className="n">{g.discount_pct ? `${g.discount_pct}%` : '—'}</td><td>{g.price_list_name ?? '—'}</td><td className="n">{g.customers}</td>
              <td><button onClick={() => setEditGroup(g)}>Edit</button> <button onClick={() => confirm(`Delete the group ${g.name}? Its ${g.customers} customers stay, with no group.`) && guard(() => api(`/api/customer-groups/${g.id}`, { method: 'DELETE' }))}>Delete</button></td></tr>))}</tbody>
        </table>{groups.data?.length === 0 && <div className="empty">No groups yet. Try one called Trade or Restaurant.</div>}</div>
      </div></div>

      <div className="panel"><div className="panel-body">
        <div className="row"><h2 className="grow" style={{ margin: 0 }}>Price lists</h2><button className="primary" onClick={() => setEditList({})}>New price list</button></div>
        <div className="table-wrap"><table className="grid">
          <thead><tr><th>Name</th><th>Note</th><th className="n">Items</th><th className="n">Used by groups</th><th /></tr></thead>
          <tbody>{lists.data?.map((l) => (
            <tr key={l.id}><td>{l.name}</td><td>{l.note ?? ''}</td><td className="n">{l.items}</td><td className="n">{l.groups}</td>
              <td><button onClick={() => setOpen(l)}>Prices</button> <button onClick={() => setEditList(l)}>Rename</button> <button onClick={() => confirm(`Delete the price list ${l.name}?`) && guard(() => api(`/api/price-lists/${l.id}`, { method: 'DELETE' }))}>Delete</button></td></tr>))}</tbody>
        </table>{lists.data?.length === 0 && <div className="empty">No price lists yet. To fill one from a spreadsheet, use Import and export.</div>}</div>
      </div></div>

      {editGroup && <GroupForm group={editGroup} lists={lists.data ?? []} onClose={() => setEditGroup(null)} onDone={() => { setEditGroup(null); reload(); }} />}
      {editList && <ListForm list={editList} onClose={() => setEditList(null)} onDone={() => { setEditList(null); reload(); }} />}
      {open && <ListPrices list={open} onClose={() => { setOpen(null); reload(); }} />}
    </div>
  );
}

function GroupForm({ group, lists, onClose, onDone }: { group: any; lists: any[]; onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({ name: group.name ?? '', pct: group.discount_pct ? String(group.discount_pct) : '', listId: group.price_list_id ?? '' });
  const [err, setErr] = useState<unknown>(null);
  const pct = f.pct === '' ? 0 : Number(f.pct);
  const ok = f.name.trim() && pct >= 0 && pct <= 100;
  return (
    <Modal title={group.id ? 'Edit group' : 'New group'} onClose={onClose} footer={<button className="primary" disabled={!ok} onClick={async () => {
      try {
        const body = { name: f.name, discountPct: pct, priceListId: f.listId || null };
        if (group.id) await api(`/api/customer-groups/${group.id}`, { method: 'PUT', body }); else await api('/api/customer-groups', { body });
        onDone();
      } catch (e) { setErr(e); }
    }}>Save</button>}>
      <ErrorNote error={err} />
      <label>Name<input autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Trade" /></label>
      <div className="field-row">
        <label>Percent off<input className="num-input" inputMode="decimal" value={f.pct} onChange={(e) => setF({ ...f, pct: e.target.value.replace(/[^\d.]/g, '') })} placeholder="0" /></label>
        <label>Price list<select value={f.listId} onChange={(e) => setF({ ...f, listId: e.target.value })}><option value="">None</option>{lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select></label>
      </div>
    </Modal>
  );
}

function ListForm({ list, onClose, onDone }: { list: any; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState(list.name ?? '');
  const [note, setNote] = useState(list.note ?? '');
  const [err, setErr] = useState<unknown>(null);
  return (
    <Modal title={list.id ? 'Rename price list' : 'New price list'} onClose={onClose} footer={<button className="primary" disabled={!name.trim()} onClick={async () => {
      try {
        const body = { name, note: note || null };
        if (list.id) await api(`/api/price-lists/${list.id}`, { method: 'PUT', body }); else await api('/api/price-lists', { body });
        onDone();
      } catch (e) { setErr(e); }
    }}>Save</button>}>
      <ErrorNote error={err} />
      <label>Name<input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Restaurant prices" /></label>
      <label>Note<input value={note} onChange={(e) => setNote(e.target.value)} placeholder="optional" /></label>
    </Modal>
  );
}

function ListPrices({ list, onClose }: { list: any; onClose: () => void }) {
  const rows = useLoad(() => api<any[]>(`/api/price-lists/${list.id}/items`), [list.id]);
  const [find, setFind] = useState('');
  const found = useLoad(() => (find.length >= 2 ? api<any[]>(`/api/items?q=${encodeURIComponent(find)}&limit=6`) : Promise.resolve([])), [find]);
  const [pick, setPick] = useState<any>(null);
  const [price, setPrice] = useState('');
  const [err, setErr] = useState<unknown>(null);
  const cents = Math.round(Number(price) * 100);
  return (
    <Modal title={`Prices: ${list.name}`} onClose={onClose} wide>
      <ErrorNote error={rows.error ?? err} />
      <div className="field-row">
        <label style={{ flex: 2 }}>Add an item<input value={pick ? `${pick.brand} ${pick.name} ${sizeLabel(pick.size_ml)}` : find} onChange={(e) => { setPick(null); setFind(e.target.value); }} placeholder="Search your items" /></label>
        <label>List price ($)<input className="num-input" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value.replace(/[^\d.]/g, ''))} /></label>
        <button className="primary" disabled={!pick || !(cents >= 0) || price === ''} onClick={async () => {
          setErr(null);
          try { await api(`/api/price-lists/${list.id}/items/${pick.product_id}`, { method: 'PUT', body: { priceCents: cents } }); setPick(null); setFind(''); setPrice(''); rows.reload(); } catch (e) { setErr(e); }
        }}>Add</button>
      </div>
      {!pick && found.data?.map((i) => <button key={i.id} className="ghost" style={{ display: 'block', width: '100%', textAlign: 'left' }} onClick={() => setPick(i)}>{i.brand} {i.name} · {sizeLabel(i.size_ml)} · shelf {usd(i.retail_cents)}</button>)}
      {!pick && <div><NewItemButton onCreated={(i) => setPick(i)} /></div>}
      <div className="table-wrap" style={{ maxHeight: 360, overflow: 'auto' }}><table className="grid">
        <thead><tr><th>Item</th><th className="n">Shelf</th><th className="n">List price</th><th /></tr></thead>
        <tbody>{rows.data?.map((r) => (
          <tr key={r.product_id}><td>{r.brand} {r.name} · {sizeLabel(r.size_ml)}</td><td className="n">{usd(r.retail_cents)}</td><td className="n">{usd(r.price_cents)}</td>
            <td><button onClick={async () => { await api(`/api/price-lists/${list.id}/items/${r.product_id}`, { method: 'DELETE' }); rows.reload(); }}>Remove</button></td></tr>))}</tbody>
      </table>{rows.data?.length === 0 && <div className="empty">No prices yet.</div>}</div>
    </Modal>
  );
}
